import { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";

const API = "https://dailydish.in/api/production/chefs";

const AVATAR_COLORS = [
  { bg: "#E1F5EE", color: "#085041" }, { bg: "#E6F1FB", color: "#185FA5" },
  { bg: "#FAEEDA", color: "#BA7517" }, { bg: "#FCEBEB", color: "#A32D2D" },
  { bg: "#F3E8FF", color: "#6B21A8" }, { bg: "#FEF3C7", color: "#92400E" },
];

function Avatar({ name, index = 0, size = 40 }) {
  const { bg, color } = AVATAR_COLORS[index % AVATAR_COLORS.length];
  const letters = name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: bg, color,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: size * 0.35, flexShrink: 0,
    }}>{letters}</div>
  );
}

function ChefRow({ chef, index, onEdit, onDelete }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "14px 20px", borderBottom: "1px solid #f0f0f0", background: "#fff",
      transition: "background 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.background = "#fafafa"}
      onMouseLeave={e => e.currentTarget.style.background = "#fff"}
    >
      <div className="d-flex align-items-center gap-3">
        <Avatar name={chef.name} index={index} />
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{chef.name}</div>
          <div style={{ fontSize: 12, color: "#aaa" }}>Chef #{index + 1}</div>
        </div>
      </div>
      <div className="d-flex gap-2">
        <Button size="sm" variant="outline-secondary" style={{ borderRadius: 8 }} onClick={() => onEdit(chef)}>Edit</Button>
        <Button size="sm" variant="outline-danger" style={{ borderRadius: 8 }} onClick={() => onDelete(chef._id)}>Delete</Button>
      </div>
    </div>
  );
}

function ChefModal({ show, onHide, initial, onSave }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (show) setName(initial?.name || ""); }, [show, initial]);

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Enter chef name");
    setSaving(true);
    try {
      const res = isEdit
        ? await axios.put(`${API}/${initial._id}`, { name: name.trim() })
        : await axios.post(API, { name: name.trim() });
      if (res.data.success) { onSave(res.data.data, isEdit); onHide(); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: "1px solid #f0f0f0" }}>
        <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>{isEdit ? "Edit Chef" : "Add Chef"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form.Group>
          <Form.Label style={{ fontWeight: 500, fontSize: 13, color: "#555" }}>Chef name</Form.Label>
          <Form.Control
            placeholder="e.g. Raju Kumar"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSave()}
            style={{ borderRadius: 8 }}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: "1px solid #f0f0f0" }}>
        <Button variant="light" onClick={onHide} style={{ borderRadius: 8 }}>Cancel</Button>
        <Button variant="success" onClick={handleSave} disabled={saving} style={{ borderRadius: 8 }}>
          {saving ? <Spinner size="sm" /> : isEdit ? "Save Changes" : "Add Chef"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function ChefsTab() {
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchChefs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      if (res.data.success) setChefs(res.data.data);
    } catch { toast.error("Failed to load chefs"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchChefs(); }, []);

  const handleSave = (saved, isEdit) => {
    if (isEdit) setChefs(prev => prev.map(c => c._id === saved._id ? saved : c));
    else setChefs(prev => [saved, ...prev]);
    toast.success(isEdit ? "Chef updated" : "Chef added");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this chef?")) return;
    try {
      await axios.delete(`${API}/${id}`);
      setChefs(prev => prev.filter(c => c._id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Chefs</h5>
          <div style={{ fontSize: 13, color: "#888" }}>Kitchen staff assigned to cook each item</div>
        </div>
        <Button variant="success" onClick={() => { setEditing(null); setShow(true); }} style={{ borderRadius: 8, fontWeight: 500 }}>+ Add Chef</Button>
      </div>

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #eee", overflow: "hidden" }}>
        {loading ? (
          <div className="text-center py-5"><Spinner /></div>
        ) : chefs.length === 0 ? (
          <div className="text-center py-5" style={{ color: "#aaa" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>👨‍🍳</div>
            <div style={{ fontWeight: 500 }}>No chefs yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Add your kitchen staff here</div>
          </div>
        ) : chefs.map((chef, i) => (
          <ChefRow key={chef._id} chef={chef} index={i} onEdit={c => { setEditing(c); setShow(true); }} onDelete={handleDelete} />
        ))}
      </div>

      <ChefModal show={show} onHide={() => setShow(false)} initial={editing} onSave={handleSave} />
    </>
  );
}
